import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface IEventDetailEntityAttributes extends IModelAttributes {
	name?: string;
	description?: string;
	googleMapUrl?: string;
	address?: string;
	totalCapacity: number;
	cancelled: boolean;
	hidden: boolean;
	closed: boolean;
	termsAndConditions?: string;

	customTicketTypes: Array<Models.CustomTicketTypeEntity | Models.ICustomTicketTypeEntityAttributes>;
	eventLabelId?: string;
	eventLabel?: Models.EventLabelEntity | Models.IEventLabelEntityAttributes;
	ferryTrip?: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	headerImage?: Models.UploadedImageEntity | Models.IUploadedImageEntityAttributes;
	supportingImages: Array<Models.UploadedImageEntity | Models.IUploadedImageEntityAttributes>;
	remainingCapacity?: number;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	tags?: string;
	isFeatured?: boolean;
	itineraryPlan?: Models.ItineraryPlanEntity | Models.IItineraryPlanEntityAttributes;
	eventSeriesId?: string;
	eventSeries?: Models.EventSeriesEntity | Models.IEventSeriesEntityAttributes;
	appliedAdjustmentType?: string;
	appliedAdjustmentValue?: number;
}

@entity('EventDetailEntity', 'Event Detail')
export default class EventDetailEntity extends Model implements IEventDetailEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The name of the event. Optional - falls back to EventSeries.name if not defined.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public name?: string;

	/**
	 * The tags for the event. Optional - falls back to EventSeries.tags if not defined.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public tags?: string;

	/**
	 * A description of the event. Optional - falls back to EventSeries.description if not defined.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public description?: string;

	/**
	 * The URL for the google map.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public googleMapUrl: string;

	@observable
	@attribute<EventDetailEntity, string>()
	public address: string;

	@observable
	@attribute<EventDetailEntity, number>()
	public totalCapacity: number;

	/**
	 * Whether or not an event has been cancelled.
	 */
	@observable
	@attribute<EventDetailEntity, boolean>()
	public cancelled: boolean;

	/**
	 * Whether or not an event is hidden from regular customers.
	 */
	@observable
	@attribute<EventDetailEntity, boolean>()
	public hidden: boolean;

	/**
	 * Whether or not the event is closed for ticket sales.
	 */
	@observable
	@attribute<EventDetailEntity, boolean>()
	public closed: boolean;

	/**
	 * The terms and conditions for the event. Optional - falls back to EventSeries.termsAndConditions if not defined.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public termsAndConditions?: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public customTicketTypes: Models.CustomTicketTypeEntity[] = [];

	@observable
	@attribute()
	public eventLabelId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventLabel: Models.EventLabelEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip?: Models.FerryTripEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public headerImage?: Models.UploadedImageEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public supportingImages: Models.UploadedImageEntity[] = [];

	public remainingCapacity?: number;

	@observable
	@attribute()
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	/**
	 * Whether or not an event is a featured experience. Optional - falls back to EventSeries.isFeatured if not defined.
	 */
	@observable
	@attribute<EventDetailEntity, boolean>()
	public isFeatured?: boolean;

	/**
	 * The itinerary plan for this event.
	 */
	@observable
	@attribute({ isReference: true })
	public itineraryPlan?: Models.ItineraryPlanEntity;

	/**
	 * The ID of the parent event series.
	 */
	@observable
	@attribute()
	public eventSeriesId?: string;

	/**
	 * The parent event series.
	 */
	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventSeries?: Models.EventSeriesEntity;

	/**
	 * The type of price adjustment applied to this occurrence.
	 */
	@observable
	@attribute<EventDetailEntity, string>()
	public appliedAdjustmentType?: string;

	/**
	 * The value of the price adjustment applied to this occurrence.
	 */
	@observable
	@attribute<EventDetailEntity, number>()
	public appliedAdjustmentValue?: number;

	constructor(attributes?: Partial<IEventDetailEntityAttributes>) {
		super(attributes);

		this.customTicketTypes = [];
		this.supportingImages = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IEventDetailEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.googleMapUrl !== undefined) {
				this.googleMapUrl = attributes.googleMapUrl;
			}
			if (attributes.address !== undefined) {
				this.address = attributes.address;
			}
			if (attributes.totalCapacity !== undefined) {
				this.totalCapacity = attributes.totalCapacity;
			}
			if (attributes.cancelled !== undefined) {
				this.cancelled = attributes.cancelled;
			}
			if (attributes.hidden !== undefined) {
				this.hidden = attributes.hidden;
			}
			if (attributes.closed !== undefined) {
				this.closed = attributes.closed;
			}
			if (attributes.termsAndConditions !== undefined) {
				this.termsAndConditions = attributes.termsAndConditions;
			}
			if (attributes.customTicketTypes !== undefined && Array.isArray(attributes.customTicketTypes)) {
				if (!this.customTicketTypes) {
					this.customTicketTypes = [];
				}
				for (const model of attributes.customTicketTypes) {
					if (model instanceof Models.CustomTicketTypeEntity) {
						this.customTicketTypes.push(model);
					} else {
						this.customTicketTypes.push(new Models.CustomTicketTypeEntity(model));
					}
				}
			}
			if (attributes.eventLabelId !== undefined) {
				this.eventLabelId = attributes.eventLabelId;
			}
			if (attributes.eventLabel !== undefined) {
				if (attributes.eventLabel === null) {
					this.eventLabel = attributes.eventLabel;
				} else if (attributes.eventLabel instanceof Models.EventLabelEntity) {
					this.eventLabel = attributes.eventLabel;
					this.eventLabelId = attributes.eventLabel.id;
				} else {
					this.eventLabel = new Models.EventLabelEntity(attributes.eventLabel);
					this.eventLabelId = this.eventLabel.id;
				}
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
				}
			}
			if (attributes.headerImage !== undefined) {
				if (attributes.headerImage === null) {
					this.headerImage = attributes.headerImage;
				} else if (attributes.headerImage instanceof Models.UploadedImageEntity) {
					this.headerImage = attributes.headerImage;
				} else {
					this.headerImage = new Models.UploadedImageEntity(attributes.headerImage);
				}
			}
			if (attributes.supportingImages !== undefined && Array.isArray(attributes.supportingImages)) {
				if (!this.supportingImages) {
					this.supportingImages = [];
				}
				for (const model of attributes.supportingImages) {
					if (model instanceof Models.UploadedImageEntity) {
						this.supportingImages.push(model);
					} else {
						this.supportingImages.push(new Models.UploadedImageEntity(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.remainingCapacity)) {
				this.remainingCapacity = attributes.remainingCapacity;
			}
			if (isNotNullOrUndefined(attributes.organisationId)) {
				this.organisationId = attributes.organisationId;
			}
			if (isNotNullOrUndefined(attributes.organisation)) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}
			if (isNotNullOrUndefined(attributes.tags)) {
				this.tags = attributes.tags;
			}
			if (isNotNullOrUndefined(attributes.isFeatured)) {
				this.isFeatured = attributes.isFeatured;
			}
			if (attributes.itineraryPlan !== undefined) {
				if (attributes.itineraryPlan === null) {
					this.itineraryPlan = attributes.itineraryPlan;
				} else if (attributes.itineraryPlan instanceof Models.ItineraryPlanEntity) {
					this.itineraryPlan = attributes.itineraryPlan;
				} else {
					this.itineraryPlan = new Models.ItineraryPlanEntity(attributes.itineraryPlan);
				}
			}
			if (isNotNullOrUndefined(attributes.eventSeriesId)) {
				this.eventSeriesId = attributes.eventSeriesId;
			}
			if (attributes.eventSeries !== undefined) {
				if (attributes.eventSeries === null) {
					this.eventSeries = attributes.eventSeries;
				} else if (attributes.eventSeries instanceof Models.EventSeriesEntity) {
					this.eventSeries = attributes.eventSeries;
					this.eventSeriesId = attributes.eventSeries.id;
				} else {
					this.eventSeries = new Models.EventSeriesEntity(attributes.eventSeries);
					this.eventSeriesId = this.eventSeries.id;
				}
			}
			if (isNotNullOrUndefined(attributes.appliedAdjustmentType)) {
				this.appliedAdjustmentType = attributes.appliedAdjustmentType;
			}
			if (isNotNullOrUndefined(attributes.appliedAdjustmentValue)) {
				this.appliedAdjustmentValue = attributes.appliedAdjustmentValue;
			}
		}
	}
}
